import { InputStepType } from "@redotech/redo-model/conversation";
import { isWithinHours } from "@redotech/redo-model/is-within-hours";
import {
  chatFlowJsonFormat,
  ChatFlowStep,
  InfoToCollect,
  Step,
} from "@redotech/redo-model/return-flow";
import { Condition } from "@redotech/redo-model/return-flow/condition";
import { TeamInfo } from "@redotech/redo-model/team";

// Extended enum that includes original InfoToCollect values plus new ones
export enum ExpandedInfoToCollect {
  // Original InfoToCollect values
  NAME = InfoToCollect.NAME,
  EMAIL = InfoToCollect.EMAIL,
  CUSTOM = InfoToCollect.CUSTOM,
  // New values for conversation step states
  CODE_VERIFICATION = "code_verification",
}

export interface CustomerResponseInfo {
  showResponseForm: boolean;
  infoToCollect: ExpandedInfoToCollect;
  placeholder: string;
}

/**
 * Converts a ChatFlowStep and InputStepType into customer response info for rendering forms
 */
export function getCustomerResponseInfoFromStep(
  currentChatFlowStep: ChatFlowStep | null,
  inputStepType?: InputStepType,
): CustomerResponseInfo | null {
  if (inputStepType) {
    switch (inputStepType) {
      case InputStepType.COLLECT_EMAIL:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.EMAIL,
          placeholder: "Enter your email here",
        };
      case InputStepType.CODE_VERIFICATION:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.CODE_VERIFICATION,
          placeholder: "Enter verification code",
        };
      case InputStepType.OFFERING_CONVERT_TO_EMAIL:
        // This state typically shows buttons/options rather than a text input
        return null;
      case InputStepType.DEFAULT:
        // Fall through to check chat flow step
        break;
    }
  }

  // Check chat flow step for regular flow-based responses
  if (!currentChatFlowStep) {
    return null;
  }

  if (currentChatFlowStep.type === Step.CUSTOMER_RESPONSE) {
    // Handle different info collection types
    switch (currentChatFlowStep.infoToCollect) {
      case InfoToCollect.EMAIL:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.EMAIL,
          placeholder: "Enter your email here",
        };
      case InfoToCollect.NAME:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.NAME,
          placeholder: "Enter your name here",
        };
      case InfoToCollect.CUSTOM:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.CUSTOM,
          placeholder: "Enter your value here",
        };
      default:
        return {
          showResponseForm: true,
          infoToCollect: ExpandedInfoToCollect.CUSTOM,
          placeholder: "Enter your value here",
        };
    }
  }

  // All other step types don't show response forms
  return null;
}

// Helper function to evaluate conditions in chat flow
export function evaluateCondition(
  currentChatFlowStep: ChatFlowStep,
  teamInfo: any,
): number {
  if (currentChatFlowStep.type === Step.CONDITION && teamInfo) {
    let afterHours = false;
    if (currentChatFlowStep.condition.type == Condition.AFTER_HOURS) {
      afterHours = !isWithinHours({
        availabilityEnabled: !!teamInfo.widgetHoursEnabled,
        timezone: teamInfo.widgetHoursTimezone || "",
        availableHours: teamInfo.widgetHours,
      });
    }
    if (Condition.evaluateChat(currentChatFlowStep.condition, { afterHours })) {
      return currentChatFlowStep.nextTrue!;
    } else {
      return currentChatFlowStep.nextFalse!;
    }
  }
  return 0;
}

export function getInitialChatFlowStep(teamInfo: TeamInfo): {
  initialChatFlowStep: number | null;
  initialMessages: string[];
} {
  let initialChatFlowStep: number | null = null;
  const initialMessages: string[] = [];

  if (teamInfo?.chatFlow) {
    const chatFlow = chatFlowJsonFormat.read(teamInfo.chatFlow);

    if (chatFlow.steps.length > 0) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      let currentStep: ChatFlowStep | null = chatFlow.steps[0];
      let stepId = 0;

      // Evaluate the chat flow to find the first step that requires human input
      while (currentStep) {
        if (currentStep.type === Step.SEND_MESSAGE) {
          // Collect the message content
          initialMessages.push(currentStep.message);
          stepId = currentStep.next;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          currentStep = chatFlow.steps[currentStep.next];
        } else if (currentStep.type === Step.CONDITION) {
          stepId = evaluateCondition(currentStep, teamInfo);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          currentStep = chatFlow.steps[stepId];
        } else {
          // Found a step that requires customer interaction
          if (currentStep.message) {
            initialMessages.push(currentStep.message);
          }
          initialChatFlowStep = stepId;
          break;
        }
      }
    }
  }

  return { initialChatFlowStep, initialMessages };
}
